import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  ChatIcon,
  ClipboardTextIcon,
  FunnelSimpleIcon,
  PencilSimpleLineIcon,
  PlusIcon,
  UserGearIcon
} from '@phosphor-icons/react';
import Button from '@/components/ui/Button';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { useSpecialist } from 'src/contexts/SpecialistContext';
import SpecialistPill from './SpecialistPill';
import { useIsFreemiumUser } from 'src/hooks/useIsFreemiumUser';

type WorkIntent = 'ask' | 'extract' | 'draft' | 'review' | 'create';

const WORK_PROMPTS: { value: WorkIntent; label: string; icon: typeof ChatIcon; phrases: string[] }[] = [
  {
    value: 'ask',
    label: 'Ask',
    icon: ChatIcon,
    phrases: [
      'What do the HTMs require for ',
      'How often should we inspect ',
      'Who is responsible for ',
      'Summarise the guidance on '
    ]
  },
  {
    value: 'extract',
    label: 'Extract',
    icon: FunnelSimpleIcon,
    phrases: ['Extract actions from ', 'Pull the recommendations out of ']
  },
  {
    value: 'draft',
    label: 'Draft',
    icon: PencilSimpleLineIcon,
    phrases: [
      'Draft a ',
      'Draft an email about ',
      'Write meeting minutes for ',
      'Compose a policy for ',
      'Write a risk assessment for '
    ]
  },
  {
    value: 'review',
    label: 'Review',
    icon: ClipboardTextIcon,
    phrases: [
      'Review this against HTM ',
      'Check this policy for gaps in ',
      'Is our group compliant with ',
      'Does this document meet '
    ]
  },
  {
    value: 'create',
    label: 'Create',
    icon: PlusIcon,
    phrases: ['Create an action for', 'Create a dashboard based on data in ', 'Produce a visualisation of']
  }
];

export default function WorkIntentButtons({
  disabled = false,
  onChoose
}: {
  disabled?: boolean;
  onChoose: (phrase: string) => void;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const [promptMenu, setPromptMenu] = useState<{ intent: WorkIntent; anchor: HTMLElement } | null>(null);
  const [specialistAnchor, setSpecialistAnchor] = useState<HTMLElement | null>(null);
  const isFreemiumUser = useIsFreemiumUser();
  const { specialists, setSelectedSpecialist, openManageModal } = useSpecialist();
  const recentSpecialists = useMemo(() => {
    const available = specialists.filter((specialist) => !specialist.deleted_at);
    const used = available
      .filter((specialist) => specialist.last_used_at)
      .sort((left, right) => new Date(right.last_used_at!).getTime() - new Date(left.last_used_at!).getTime())
      .slice(0, 3);
    if (used.length > 0) return used;
    return [...available].sort((left, right) => left.name.localeCompare(right.name)).slice(0, 3);
  }, [specialists]);
  const showSpecialistButton = !disabled && !isFreemiumUser;
  const phrases = WORK_PROMPTS.find((item) => item.value === promptMenu?.intent)?.phrases ?? [];

  useLayoutEffect(() => {
    const row = rowRef.current;
    const measureRow = measureRef.current;
    if (!row || !measureRow) return;

    const measure = (sync: boolean) => {
      const buttons = [...measureRow.querySelectorAll<HTMLElement>('[data-work-intent]')];
      if (buttons.length === 0 || buttons.every((button) => button.offsetWidth === 0)) return;
      const styles = window.getComputedStyle(measureRow);
      const gap = Number.parseFloat(styles.columnGap) || 0;
      const available = row.clientWidth;
      let used = 0;
      let fit = 0;
      for (const button of buttons) {
        const next = fit === 0 ? button.offsetWidth : used + gap + button.offsetWidth;
        if (next > available + 1) break;
        used = next;
        fit += 1;
      }
      const update = () => setVisibleCount((current) => (current === fit ? current : fit));
      if (sync) flushSync(update);
      else update();
    };

    measure(false);
    const observer = new ResizeObserver(() => measure(true));
    observer.observe(row);
    return () => observer.disconnect();
  }, [showSpecialistButton]);

  return (
    <>
      <div className="relative w-full min-w-0">
        <div
          ref={measureRef}
          className="pointer-events-none invisible absolute left-0 top-0 flex w-max gap-2"
          aria-hidden
        >
          {WORK_PROMPTS.map((item) => (
            <Button
              key={item.value}
              type="button"
              variant="outline"
              size="md"
              icon={item.icon}
              data-work-intent=""
              tabIndex={-1}
            >
              {item.label}
            </Button>
          ))}
          {showSpecialistButton ? (
            <Button type="button" variant="outline" size="md" icon={UserGearIcon} data-work-intent="" tabIndex={-1}>
              Use a specialist
            </Button>
          ) : null}
        </div>
        <div
          ref={rowRef}
          className="flex w-full min-w-0 flex-nowrap gap-2 overflow-hidden"
          role="group"
          aria-label="What would you like to work on?"
        >
          {WORK_PROMPTS.map((item, index) => (
            <Button
              key={item.value}
              type="button"
              variant="outline"
              size="md"
              icon={item.icon}
              className={index >= visibleCount ? 'hidden' : 'shrink-0'}
              disabled={disabled}
              aria-expanded={promptMenu?.intent === item.value}
              aria-haspopup="menu"
              onClick={(event) => {
                const anchor = event.currentTarget;
                if (!(anchor instanceof HTMLElement)) return;
                setSpecialistAnchor(null);
                setPromptMenu((current) => (current?.intent === item.value ? null : { intent: item.value, anchor }));
              }}
            >
              {item.label}
            </Button>
          ))}
          {showSpecialistButton ? (
            <Button
              type="button"
              variant="outline"
              size="md"
              icon={UserGearIcon}
              className={WORK_PROMPTS.length >= visibleCount ? 'hidden' : 'shrink-0'}
              disabled={disabled}
              aria-expanded={specialistAnchor != null}
              aria-haspopup="menu"
              onClick={(event) => {
                const anchor = event.currentTarget;
                if (!(anchor instanceof HTMLElement)) return;
                setPromptMenu(null);
                setSpecialistAnchor((current) => (current ? null : anchor));
              }}
            >
              Use a specialist
            </Button>
          ) : null}
        </div>
      </div>
      <Menu
        open={specialistAnchor != null}
        anchorEl={specialistAnchor}
        onClose={() => setSpecialistAnchor(null)}
        placement="bottom-start"
        className="w-[280px]"
      >
        {recentSpecialists.length === 0 ? (
          <MenuItem disabled>No specialists yet</MenuItem>
        ) : (
          recentSpecialists.map((specialist) => (
            <MenuItem
              key={specialist.id}
              onClick={() => {
                setSelectedSpecialist(specialist);
                setSpecialistAnchor(null);
              }}
            >
              <SpecialistPill specialist={specialist} className="min-w-0" />
            </MenuItem>
          ))
        )}
        <MenuItem
          onClick={() => {
            setSpecialistAnchor(null);
            openManageModal?.();
          }}
        >
          Show all
        </MenuItem>
      </Menu>
      <Menu
        open={promptMenu != null && phrases.length > 0}
        anchorEl={promptMenu?.anchor ?? null}
        onClose={() => setPromptMenu(null)}
        placement="bottom-start"
        className="w-[280px]"
      >
        {phrases.map((phrase) => (
          <MenuItem
            key={phrase}
            onClick={() => {
              setPromptMenu(null);
              onChoose(phrase);
            }}
          >
            {phrase.trim()}...
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
